import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useStoreState, useStoreActions } from 'easy-peasy';

import Homepage from './pages/homepage/Homepage';
import 'react-calendar/dist/Calendar.css';
import Login from './pages/login/Login';
import Register from './pages/register/Register';
import { Toaster } from 'react-hot-toast';
import Loader from './components/loader/Loader';
import { auth, database } from './helper/firebase.helper';
import RouteGuard from './components/RouteGuard';
import { getIdTokenResult, onAuthStateChanged, signOut } from 'firebase/auth';
import Dashboard from './pages/dashboard/Dashboard';
import { getOrg } from './helper/api.helper';
import { getOrgCode } from './helper/utils.helper';
import Certificate from './pages/certificate/Certificate';
import PageNotFound from './pages/shared/404';
import Feedback from './pages/feedback/Feedback';
import IntakeForm from './pages/intake-form/IntakeForm';

function App() {
  const setUser = useStoreActions((actions) => actions.setUser);
  const setLogo = useStoreActions((actions) => actions.setLogo);
  const setOrgId = useStoreActions((actions) => actions.setOrgId);
  const setBanner = useStoreActions((actions) => actions.setBanner);
  const setQualified = useStoreActions((actions) => actions.setQualified);
  const setHelpline = useStoreActions((actions) => actions.setHelpline);
  const setEmailDomains = useStoreActions((actions) => actions.setEmailDomains);
  const setClientData = useStoreActions((actions) => actions.setClientData);
  const { pathname } = useLocation();
  const [orgLoaded, setOrgLoaded] = useState(false);
  const [orgFailed, setOrgFailed] = useState(false);

  const user = useStoreState((state) => state.user);
  const isUserAuthenticated = user;

  useEffect(() => {
    const orgCode = getOrgCode();
    Promise.all([
      ...(user
        ? [
            getDoc(doc(database, `clients/${user.uid}`))
              .then((res) => {
                setClientData({
                  id: res.id,
                  ...res.data()
                });
              })
              .catch((err) => console.error(err))
          ]
        : []),
      getOrg(orgCode)
        .then((res) => res.json())
        .then((res) => {
          if (res.status === 'success') {
            setLogo(res.data.logo);
            setOrgId(res.data.orgId);
            setBanner(res.data.banner);
            setQualified(res.data.qualified);
            setHelpline(res.data.helpline);
            setEmailDomains(res.data.emailDomains);
            setOrgFailed(false);
          } else {
            setOrgFailed(true);
          }
        })
        .catch(() => {
          setOrgFailed(true);
        })
        .finally(() => {
          setOrgLoaded(true);
        })
    ]);
  }, [user]);

  useEffect(() => {
    onAuthStateChanged(auth, async (usr) => {
      if (usr) {
        getIdTokenResult(usr, true).then((res) => {
          if (res.claims?.userType === 0) {
            setUser({
              ...usr,
              orgId: res.claims.orgId,
              userType: res.claims.userType
            });
          } else {
            setUser(null);
          }
        });
      } else {
        setUser(usr);
      }
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Suspense fallback={<Loader />}>
      {user === false || !orgLoaded ? (
        <Loader />
      ) : orgFailed ? (
        <PageNotFound />
      ) : (
        <Routes>
          <Route
            path="/login/*"
            element={
              <RouteGuard
                isAuthenticated={!isUserAuthenticated}
                redPath="/"
                returnToFrom
                component={Login}
              />
            }
          />
          <Route path="/register/*" element={<Register />} />
          <Route
            path="/dashboard/*"
            element={
              <RouteGuard
                isAuthenticated={isUserAuthenticated}
                redPath="/login"
                component={Dashboard}
              />
            }
          />
          <Route path="/*" element={<Homepage />} />
          <Route path="/certificates/:certificateId" element={<Certificate />} />
          <Route path="/feedback/:feedbackId" element={<Feedback />} />
          <Route path="/forms/:clientId/:formId" element={<IntakeForm />} />
          <Route path="/404" element={<PageNotFound />} />
        </Routes>
      )}
      <Toaster position="bottom-center" />
    </Suspense>
  );
}

export default App;
